import {
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

/** Bootstrap-like delay: show after long hover; hide quickly. */
export const TOOLTIP_SHOW_MS = 1500;
export const TOOLTIP_HIDE_MS = 100;

type TipPlacement = 'top' | 'bottom' | 'left' | 'right';

type TipState = {
  content: ReactNode;
  left: number;
  top: number;
  placement: TipPlacement;
  anchor: DOMRect;
  /** Identity for remeasure when content shape changes */
  key: string;
};

type DiagramTooltipApi = {
  scheduleShow: (content: ReactNode, anchor: DOMRect, key?: string) => void;
  hide: () => void;
  cancel: () => void;
};

const DiagramTooltipContext = createContext<DiagramTooltipApi | null>(null);

function placeTooltip(
  anchor: DOMRect,
  tipW: number,
  tipH: number,
): { left: number; top: number; placement: TipPlacement } {
  const gap = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const cx = anchor.left + anchor.width / 2;
  const cy = anchor.top + anchor.height / 2;
  const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

  const candidates: Array<{ placement: TipPlacement; left: number; top: number }> = [
    { placement: 'top', left: cx - tipW / 2, top: anchor.top - tipH - gap },
    { placement: 'bottom', left: cx - tipW / 2, top: anchor.bottom + gap },
    { placement: 'right', left: anchor.right + gap, top: cy - tipH / 2 },
    { placement: 'left', left: anchor.left - tipW - gap, top: cy - tipH / 2 },
  ];

  for (const c of candidates) {
    const fits =
      c.left >= 4 && c.top >= 4 && c.left + tipW <= vw - 4 && c.top + tipH <= vh - 4;
    if (fits) return c;
  }

  const first = candidates[0];
  return {
    placement: first.placement,
    left: clamp(first.left, 8, Math.max(8, vw - tipW - 8)),
    top: clamp(first.top, 8, Math.max(8, vh - tipH - 8)),
  };
}

function isEmptyContent(content: ReactNode): boolean {
  if (content == null || content === false) return true;
  if (typeof content === 'string') return !content.trim();
  return false;
}

export function DiagramTooltipProvider({ children }: { children: ReactNode }) {
  const [tip, setTip] = useState<TipState | null>(null);
  const showTimer = useRef<number | null>(null);
  const hideTimer = useRef<number | null>(null);
  const pending = useRef<{ content: ReactNode; anchor: DOMRect; key: string } | null>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  const clearShowTimer = useCallback(() => {
    if (showTimer.current != null) {
      window.clearTimeout(showTimer.current);
      showTimer.current = null;
    }
  }, []);

  const clearHideTimer = useCallback(() => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  }, []);

  const cancel = useCallback(() => {
    clearShowTimer();
    clearHideTimer();
    pending.current = null;
    setTip(null);
  }, [clearHideTimer, clearShowTimer]);

  const hide = useCallback(() => {
    clearShowTimer();
    pending.current = null;
    clearHideTimer();
    hideTimer.current = window.setTimeout(() => {
      setTip(null);
      hideTimer.current = null;
    }, TOOLTIP_HIDE_MS);
  }, [clearHideTimer, clearShowTimer]);

  const scheduleShow = useCallback(
    (content: ReactNode, rect: DOMRect, key = 'tip') => {
      if (isEmptyContent(content)) {
        cancel();
        return;
      }
      clearHideTimer();
      clearShowTimer();
      pending.current = { content, anchor: rect, key };
      showTimer.current = window.setTimeout(() => {
        const p = pending.current;
        if (!p) return;
        const rough = placeTooltip(p.anchor, 260, 220);
        setTip({ content: p.content, anchor: p.anchor, key: p.key, ...rough });
        showTimer.current = null;
      }, TOOLTIP_SHOW_MS);
    },
    [cancel, clearHideTimer, clearShowTimer],
  );

  useEffect(() => {
    if (!tip || !tipRef.current) return;
    const r = tipRef.current.getBoundingClientRect();
    const next = placeTooltip(tip.anchor, r.width, r.height);
    if (next.left !== tip.left || next.top !== tip.top || next.placement !== tip.placement) {
      setTip((prev) => (prev ? { ...prev, ...next } : prev));
    }
  }, [tip?.key, tip?.anchor]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(
    () => () => {
      clearShowTimer();
      clearHideTimer();
    },
    [clearHideTimer, clearShowTimer],
  );

  const api = useMemo(() => ({ scheduleShow, hide, cancel }), [scheduleShow, hide, cancel]);

  return (
    <DiagramTooltipContext.Provider value={api}>
      {children}
      {tip
        ? createPortal(
            <div
              ref={tipRef}
              role="tooltip"
              className={`ukrop-tooltip ukrop-tooltip-${tip.placement}`}
              style={{ left: tip.left, top: tip.top }}
            >
              {tip.content}
            </div>,
            document.body,
          )
        : null}
    </DiagramTooltipContext.Provider>
  );
}

export function useDiagramTooltip(): DiagramTooltipApi {
  const ctx = useContext(DiagramTooltipContext);
  if (!ctx) {
    return {
      scheduleShow: () => undefined,
      hide: () => undefined,
      cancel: () => undefined,
    };
  }
  return ctx;
}

/** Attach delayed tooltip (Bootstrap-like show delay 1.5s). */
export function useInfoTooltipHandlers(getContent: () => ReactNode) {
  const { scheduleShow, hide, cancel } = useDiagramTooltip();
  const getContentRef = useRef(getContent);
  getContentRef.current = getContent;

  return useMemo(
    () => ({
      onMouseEnter: (e: { currentTarget: EventTarget & Element }) => {
        const content = getContentRef.current();
        if (isEmptyContent(content)) return;
        scheduleShow(content, e.currentTarget.getBoundingClientRect());
      },
      onMouseLeave: () => hide(),
      onPointerDown: () => cancel(),
    }),
    [scheduleShow, hide, cancel],
  );
}

type TipChildProps = {
  onMouseEnter?: (e: MouseEvent) => void;
  onMouseLeave?: (e: MouseEvent) => void;
  onPointerDown?: (e: PointerEvent) => void;
};

/** Same delayed bubble as diagram tooltips, for any UI control that used native title. */
export function UkropTip({
  text,
  children,
}: {
  text?: ReactNode;
  children: ReactElement;
}) {
  const handlers = useInfoTooltipHandlers(() => {
    if (text == null || text === false) return null;
    if (typeof text === 'string' && !text.trim()) return null;
    return text;
  });

  if (!isValidElement(children)) return children;
  if (text == null || text === false || (typeof text === 'string' && !text.trim())) {
    return children;
  }

  const prev = children.props as TipChildProps;
  return cloneElement(children, {
    onMouseEnter: (e: MouseEvent) => {
      handlers.onMouseEnter(e);
      prev.onMouseEnter?.(e);
    },
    onMouseLeave: (e: MouseEvent) => {
      handlers.onMouseLeave();
      prev.onMouseLeave?.(e);
    },
    onPointerDown: (e: PointerEvent) => {
      handlers.onPointerDown();
      prev.onPointerDown?.(e);
    },
  } as TipChildProps);
}
